import { memo } from 'react'
import { Badge, Box, HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { FiBriefcase, FiCheck, FiHome } from 'react-icons/fi'
import { stopAlreadyVoted, type WalkStop } from '../../../lib/walkStops'

/** "Maria R., Dan R. +1" — at most two names, then a count. */
export function residentsSummary(stop: WalkStop): string {
  const names = stop.residents.map(r => `${r.firstName} ${r.lastName ? `${r.lastName[0]}.` : ''}`.trim())
  return names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`
}

/**
 * [Spec §4.3] One walk-list row. Rows SELECT (they drive the dock); they never navigate.
 * ≥ 56px, `role="button"` + `aria-pressed` so the selection is announced.
 * Memoised, and `onSelect` is the list's stable callback called with this row's key (and person,
 * in Contacts view) — no per-render closures, so a GPS fix re-renders no rows (I-7).
 */
export const WalkStopRow = memo(function WalkStopRow({ stop, contactId, seq, selected, showKindIcon, primary, secondary, onSelect }: {
  stop: WalkStop
  /** Contacts view: the person this row stands for. */
  contactId?: string
  seq: number | undefined; selected: boolean; showKindIcon: boolean
  primary: string; secondary: string; onSelect: (key: string, contactId?: string) => void
}) {
  const done = stop.state === 'done'
  const select = () => onSelect(stop.key, contactId)
  return (
    <Box role="button" aria-pressed={selected} tabIndex={0} onClick={select}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select() } }}
      minH="56px" px={3} py={2} bg="surface" borderRadius="md" cursor="pointer"
      display="flex" alignItems="center"
      borderWidth={selected ? '2px' : '1px'} borderColor={selected ? 'accent' : 'border'}
      opacity={done ? 0.7 : 1}>
      <HStack gap={3} w="full">
        <Box minW="32px" h="32px" borderRadius="full" display="grid" placeItems="center" fontWeight="bold" fontSize="sm"
          bg={done ? 'bg.subtle' : 'accent.soft'} color={done ? 'fg.muted' : 'accent'}>
          {done ? <Box as="span" role="img" aria-label="Done" display="inline-flex"><Icon as={FiCheck} /></Box> : seq}
        </Box>
        {showKindIcon && (
          // Chakra's Icon is aria-hidden; the labelled wrapper is what assistive tech announces.
          <Box as="span" role="img" aria-label={stop.kind === 'worksite' ? 'Worksite' : 'Home'}
            display="inline-flex" color="fg.muted">
            <Icon as={stop.kind === 'worksite' ? FiBriefcase : FiHome} />
          </Box>
        )}
        <VStack align="start" gap={0} flex={1} minW={0}>
          <Text fontWeight="semibold" lineClamp={1}>{primary}</Text>
          {secondary && <Text fontSize="sm" color="fg.muted" lineClamp={1}>{secondary}</Text>}
          {stop.movedFrom && <Text fontSize="xs" color="warning.fg">{`Moved from ${stop.movedFrom}`}</Text>}
          {/* [CF-1 P5] Advice, not a filter: the stop stays tappable and keeps its place. Sits under the address so it wraps at 390px. */}
          {stopAlreadyVoted(stop, contactId) && (
            <Badge bg="info.subtle" color="info.fg" mt={1} whiteSpace="normal">Already voted — skip</Badge>
          )}
        </VStack>
        {stop.kind === 'home' && stop.hasDnc && <Badge bg="danger.subtle" color="danger.fg">DNC</Badge>}
        {stop.state === 'revisit' && <Badge bg="warning.subtle" color="warning.fg">Revisit</Badge>}
        {stop.visitedToday && <Badge bg="bg.subtle" color="fg.muted">Visited today</Badge>}
        {done && !stop.visitedToday && <Text fontSize="xs" color="fg.muted">Done</Text>}
      </HStack>
    </Box>
  )
})
